import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ValidationForm, TextInput, SelectGroup, Checkbox } from 'react-bootstrap4-form-validation';
import { useDispatch, useSelector } from 'react-redux';
import DataGrid, {
    Column,
    Paging,
    Pager,
    Scrolling,
    SearchPanel,
    FilterRow,
    HeaderFilter,
    ColumnChooser,
    ColumnFixing,
    Button as ActionButton,
    Export,
    Summary,
    TotalItem
} from 'devextreme-react/data-grid';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Loader from '../App/layout/Loader';
import SearchableDropDown from '../App/components/Common/SearchableDropDown';
import { GATE_PASS } from '../store/programs/GatePass/gatePass.reducer';
import { NAVIGATE_GATE_PASS_MASTER } from '../store/programs/GatePass/gatePass.actionsType';
import {
    fetchGatePassDetails,
    createGatePassDetails,
    updateGatePassDetails,
    deleteGatePassDetails,
    createGatePassDetailsForm,
    setGatePassDetailsForm,
    resetGatePassDetailsForm
} from '../store/programs/GatePass/gatePassDetails.actions';
import { getIpAddress } from '../store/propertyMaster/propertyMaster.actions';
import { PROPERTY_MASTER } from '../store/propertyMaster/propertyMaster.reducer';
import { COMMON_PROPERTY } from '../store/property/common/commonProperty.reducer';
import { fetchCommonPropertyById } from '../store/property/common/commonProperty.actions';
import { HSN_MASTER } from '../store/hsnMaster/hsnMaster.reducer';
import { fetchHSNMaster } from '../store/hsnMaster/hsnMaster.actions';
import { COLOR_DETAILS } from '../store/colorDetails/colorDetails.reducer';
import { fetchColorDetailsWithoutId } from '../store/colorDetails/colorDetails.actions';
import { ACCESSORIS_MASTER } from '../store/accessoriesMaster/accessoriesMaster.reducer';
import { fetchPropertyDataForAccessoriesMaster } from '../store/accessoriesMaster/accessoriesMaster.actions';

const allowedPageSizes = [10, 20, 50, 100];
const money = { type: 'fixedPoint', precision: 2 };
const num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));
const round2 = (v) => Math.round(num(v) * 100) / 100;

/**
 * All the calculated values of a detail row, worked out from
 * Quantity × Rate, Discount and the GST %s. Same maths as the General DC
 * details form, kept in one place so the screen and the saved row match.
 */
const calcTotals = (f) => {
    const gross = num(f.fldQuantity) * num(f.fldRate);
    const discountValue = num(f.fldDiscountPercentage) > 0 ? (gross * num(f.fldDiscountPercentage)) / 100 : num(f.fldDiscountValue);
    const taxable = gross - discountValue;
    const igst = (taxable * num(f.fldIGSTPercentage)) / 100;
    const sgst = (taxable * num(f.fldSGSTPercentage)) / 100;
    const cgst = (taxable * num(f.fldCGSTPercentage)) / 100;
    const gst = igst + sgst + cgst;
    return {
        fldGrossValue: round2(gross),
        fldDiscountValue: round2(discountValue),
        fldTaxableValue: round2(taxable),
        fldIGSTValue: round2(igst),
        fldSGSTValue: round2(sgst),
        fldCGSTValue: round2(cgst),
        fldGSTValue: round2(gst),
        fldTotalValue: round2(taxable + gst)
    };
};

const GatePassDetails = () => {
    const dispatch = useDispatch();
    const reset = useRef();
    const [showModal, setShowModal] = useState(false);
    const [mode, setMode] = useState('create'); // 'create' | 'update'
    const [keepValues, setKeepValues] = useState(false); // "Clear All Data" unticked → keep values after Save & Add New

    const { selectedGatePass, gatePassDetails, gatePassDetailsForm: form, detailsLoading } = useSelector((state) => state[GATE_PASS]);
    const { ipAddressInfo } = useSelector((state) => state[PROPERTY_MASTER]);
    const { unitOfMeasurement, generalheader, generalItem } = useSelector((state) => state[COMMON_PROPERTY]);
    const { hsnMaster } = useSelector((state) => state[HSN_MASTER]);
    const { colorDetailsWithoutId } = useSelector((state) => state[COLOR_DETAILS]);
    const { accessoriesSize, accessoriesCategory } = useSelector((state) => state[ACCESSORIS_MASTER]);

    const gp = selectedGatePass || {};
    const gatePassId = gp.fldId;
    // Header is locked once goods are inwarded — same rule as General DC.
    const isLocked = num(gp.fldInwardQty) > 0;
    const totals = calcTotals(form);

    useEffect(() => {
        dispatch(fetchGatePassDetails(gatePassId));
        dispatch(getIpAddress());
        dispatch(fetchCommonPropertyById(22)); // UOM
        dispatch(fetchCommonPropertyById(65)); // General Header (Program)
        dispatch(fetchCommonPropertyById(66)); // General Item
        dispatch(fetchHSNMaster());
        dispatch(fetchColorDetailsWithoutId());
        dispatch(fetchPropertyDataForAccessoriesMaster(47)); // Size
        dispatch(fetchPropertyDataForAccessoriesMaster(44)); // Accessories category (Item when Program = ACCESSORIES)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatePassId]);

    const setField = (key, value) => dispatch(createGatePassDetailsForm(key, value));

    // HSN → GST split: same state (Unit vs Supplier) = SGST + CGST, else IGST.
    const applyHsn = (hsnId) => {
        setField('fldFKHSNCode', hsnId);
        const hsn = (hsnMaster || []).find((h) => String(h.fldId) === String(hsnId));
        const pct = num(hsn?.fldSalesPct);
        const unitState = (gp.fldUnitState || '').toUpperCase().trim();
        const supplierState = (gp.fldSupplierState || '').toUpperCase().trim();
        if (unitState && supplierState && unitState === supplierState) {
            setField('fldIGSTPercentage', 0);
            setField('fldSGSTPercentage', pct / 2);
            setField('fldCGSTPercentage', pct / 2);
        } else {
            setField('fldIGSTPercentage', pct);
            setField('fldSGSTPercentage', 0);
            setField('fldCGSTPercentage', 0);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'fldFKGeneralHeader') {
            const header = (generalheader || []).find((x) => String(x.fldId) === String(value));
            setField('fldGeneralHeader', header?.fldDescription?.toUpperCase() || '');
            // Item list depends on the Program, so clear the old item
            setField('fldFKGeneralItem', '');
            setField('fldGeneralItem', '');
        }
        if (name === 'fldDiscountPercentage' && num(value) > 0) {
            setField('fldDiscountValue', '');
        }
        setField(name, value);
    };

    const itemOptions = form.fldGeneralHeader === 'ACCESSORIES' ? accessoriesCategory || [] : generalItem || [];

    const openCreate = () => {
        if (isLocked) {
            toast.error('ADD not possible now — goods already inwarded for this Gate Pass.', { autoClose: 2000 });
            return;
        }
        setMode('create');
        dispatch(resetGatePassDetailsForm());
        setShowModal(true);
    };

    const openEdit = (row) => {
        setMode('update');
        // API rows may come back as FldFKSize (PascalCase) — make every key start lower-case
        // so they match the form's field names (fldFKSize).
        const data = Object.keys(row || {}).reduce((acc, k) => {
            acc[k.charAt(0).toLowerCase() + k.slice(1)] = row[k];
            return acc;
        }, {});
        // If the saved row has only the size text (fldSize) and no size id,
        // find the id from the size list so the Size dropdown shows it.
        if (!data.fldFKSize && data.fldSize) {
            const size = (accessoriesSize || []).find(
                (x) => String(x.fldDescription).trim().toUpperCase() === String(data.fldSize).trim().toUpperCase()
            );
            if (size) data.fldFKSize = size.fldId;
        }
        dispatch(setGatePassDetailsForm(data));
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        dispatch(resetGatePassDetailsForm());
    };

    const confirmDelete = (row) => {
        Swal.fire({
            title: 'Delete this item?',
            text: `${row.fldGeneralItem || ''} ${row.fldItemDescription ? `- ${row.fldItemDescription}` : ''}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it'
        }).then((r) => {
            if (r.isConfirmed) dispatch(deleteGatePassDetails(row.fldId, gatePassId));
        });
    };

    const requiredMissing = () => {
        const missing = [];
        if (!form.fldFKGeneralHeader) missing.push('Program');
        if (!form.fldFKGeneralItem) missing.push('Item');
        if (!form.fldFKHSNCode) missing.push('HSN Code');
        if (!form.fldFKColour) missing.push('Color');
        if (!form.fldFKSize) missing.push('Size');
        if (!form.fldFKUOM) missing.push('UOM');
        if (num(form.fldQuantity) <= 0) missing.push('Quantity');
        return missing;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missing = requiredMissing();
        if (missing.length) {
            toast.error(`Please fill: ${missing.join(', ')}`, { autoClose: 2500 });
            return;
        }
        const button = e?.nativeEvent?.submitter?.name;

        // Header link + style / sale order copied from the Gate Pass, like General DC does.
        const payload = {
            ...form,
            ...totals,
            fldFKDCNO: gatePassId,
            fldGatePassNo: gp.fldGatePassNo,
            fldFKSaleOrder: gp.fldFKSaleOrder,
            fldSaleOrder: gp.fldSaleOrder,
            fldFKStyle: gp.fldFKStyle,
            fldStyle: gp.fldStyle,
            fldFKMainStyle: gp.fldFKMainStyle,
            fldMainStyle: gp.fldMainStyle,
            ipv4: ipAddressInfo?.IPv4 ?? null
        };

        if (mode === 'update') {
            const ok = await dispatch(updateGatePassDetails(payload, gatePassId));
            if (ok) closeModal();
            return;
        }

        const ok = await dispatch(createGatePassDetails(payload, gatePassId));
        if (!ok) return;
        if (button === 'Create') {
            closeModal();
        } else {
            if (!keepValues) dispatch(resetGatePassDetailsForm());
            reset.current?.resetValidationState(true);
        }
    };

    const renderTitleHeader = (data) => <div style={{ fontWeight: 'bold' }}>{data.column.caption}</div>;

    const readOnlyAmount = (id, label, value) => (
        <Form.Group as={Col} md="3">
            <Form.Label htmlFor={id}>{label}</Form.Label>
            <input id={id} className="form-control" style={{ textAlign: 'right' }} disabled value={Number(value || 0).toFixed(2)} />
        </Form.Group>
    );

    return (
        <Row className="d-flex flex-column">
            {detailsLoading && <Loader />}
            <Col>
                <button
                    className="addBtnPosition btn btn-primary"
                    style={isLocked ? { opacity: 0.2 } : undefined}
                    onClick={openCreate}
                >
                    <span className="plusAdd">+</span>
                    <span className="addAdd">ADD</span>
                </button>

                <Card>
                    <Card.Header>
                        <Card.Title as="h5" style={{ color: 'blue', fontWeight: 'bold', display: 'flex', justifyContent: 'center' }}>
                            <span>Gate Pass Details of {gp.fldSupplierName || ''}</span>
                            <span style={{ marginLeft: 'auto' }}>Gate Pass No: {gp.fldGatePassNo || '-'}</span>
                        </Card.Title>
                        <Row>
                            <Col>
                                <Button variant="primary" size="sm" onClick={() => dispatch({ type: NAVIGATE_GATE_PASS_MASTER })}>
                                    BACK
                                </Button>
                            </Col>
                        </Row>
                        <Row className="mt-2" style={{ fontSize: 13 }}>
                            <Col md="3"><b>Delivery At:</b> {gp.fldDeliveryAtName || '-'}</Col>
                            <Col md="3"><b>Style:</b> {gp.fldStyleNumber || gp.fldMainStyle || '-'} {gp.fldStyle ? `/ ${gp.fldStyle}` : ''}</Col>
                            <Col md="3"><b>Sales Order:</b> {gp.fldSaleOrder || gp.fldSaleOrderNumber || '-'}</Col>
                            <Col md="3"><b>Vehicle No:</b> {gp.fldVehicleNO || '-'}</Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        <DataGrid
                            dataSource={gatePassDetails}
                            keyExpr="fldId"
                            showBorders={true}
                            columnAutoWidth={true}
                            allowColumnResizing={true}
                            columnResizingMode="widget"
                            rowAlternationEnabled={true}
                            hoverStateEnabled={true}
                            noDataText="No items added yet. Click ADD to add one."
                        >
                            <FilterRow visible={true} applyFilter="auto" />
                            <HeaderFilter visible={true} />
                            <SearchPanel visible={true} width={240} placeholder="Search..." />
                            <ColumnChooser enabled={true} />
                            <ColumnFixing enabled={true} />
                            <Scrolling rowRenderingMode="virtual" />
                            <Paging defaultPageSize={10} />
                            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} showInfo={true} />

                            <Column headerCellRender={renderTitleHeader} dataField="fldGeneralHeader" caption="Program" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldGeneralItem" caption="Item" />
                            <Column headerCellRender={renderTitleHeader} dataField="tblHSNCode.fldSalesCode" caption="HSN Code" />
                            <Column headerCellRender={renderTitleHeader} dataField="tblUOM.fldDescription" caption="UOM" />
                            <Column headerCellRender={renderTitleHeader} dataField="tblColourDetailId.fldDescription" caption="Color" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldSize" caption="Size" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldItemDescription" caption="Item Description" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldQuantity" caption="Quantity" />
                            {/* <Column headerCellRender={renderTitleHeader} dataField="fldRate" format={money} caption="Rate" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldGrossValue" format={money} caption="Gross Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldDiscountPercentage" caption="Discount %" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldDiscountValue" format={money} caption="Discount Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldTaxableValue" format={money} caption="Taxable Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldIGSTPercentage" caption="IGST%" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldIGSTValue" format={money} caption="IGST Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldSGSTPercentage" caption="SGST%" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldSGSTValue" format={money} caption="SGST Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldCGSTPercentage" caption="CGST%" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldCGSTValue" format={money} caption="CGST Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldGSTValue" format={money} caption="GST Value" />
                            <Column headerCellRender={renderTitleHeader} dataField="fldTotalValue" format={money} caption="Total Value" /> */}
                            {/* <Column headerCellRender={renderTitleHeader} dataField="fldRemarks" caption="Remarks" /> */}
                            <Column headerCellRender={renderTitleHeader} caption="Actions" type="buttons" width={110}>
                                <ActionButton text="Edit" icon="edit" hint="Edit" onClick={(e) => openEdit(e.row.data)} />
                                <ActionButton
                                    cssClass="text-danger"
                                    text="Delete"
                                    icon="trash"
                                    hint="Delete"
                                    visible={!isLocked}
                                    onClick={(e) => confirmDelete(e.row.data)}
                                />
                            </Column>

                            <Summary>
                                <TotalItem column="fldQuantity" summaryType="sum" displayFormat="{0}" />
                                <TotalItem column="fldTaxableValue" summaryType="sum" valueFormat={money} displayFormat="{0}" />
                                <TotalItem column="fldGSTValue" summaryType="sum" valueFormat={money} displayFormat="{0}" />
                                <TotalItem column="fldTotalValue" summaryType="sum" valueFormat={money} displayFormat="{0}" />
                            </Summary>
                            <Export enabled={true} />
                        </DataGrid>
                    </Card.Body>
                </Card>
            </Col>

            <Modal show={showModal} onHide={closeModal} size="xl" scrollable centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title as="h5">
                        {mode === 'update' ? 'Update' : 'Create'} Gate Pass Details
                        <span style={{ fontSize: 13, marginLeft: 15, color: 'grey' }}>Gate Pass No: {gp.fldGatePassNo || '-'}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ValidationForm ref={reset} onSubmit={handleSubmit} onErrorSubmit={() => toast.error('Validation Error cannot Proceed!', { autoClose: 1500 })}>
                        <Form.Row>
                            <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldFKGeneralHeader">
                                    Program<span className="text-danger">*</span>
                                </Form.Label>
                                <SelectGroup
                                    name="fldFKGeneralHeader"
                                    id="fldFKGeneralHeader"
                                    value={form.fldFKGeneralHeader ?? ''}
                                    required
                                    errorMessage="Please select Program."
                                    onChange={handleChange}
                                >
                                    <option value="">Please select</option>
                                    {(generalheader || []).map((item, i) => (
                                        <option key={`${item.fldId}-${i}`} value={item.fldId}>
                                            {item.fldDescription}
                                        </option>
                                    ))}
                                </SelectGroup>
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldFKGeneralItem">
                                    Item<span className="text-danger">*</span>
                                </Form.Label>
                                <SelectGroup
                                    name="fldFKGeneralItem"
                                    id="fldFKGeneralItem"
                                    value={form.fldFKGeneralItem ?? ''}
                                    required
                                    errorMessage="Please select Item."
                                    onChange={(e) => {
                                        const item = itemOptions.find((x) => String(x.fldId) === String(e.target.value));
                                        setField('fldFKGeneralItem', e.target.value);
                                        setField('fldGeneralItem', item?.fldDescription || '');
                                    }}
                                >
                                    <option value="">Please select</option>
                                    {itemOptions.map((item, i) => (
                                        <option key={`${item.fldId}-${i}`} value={item.fldId}>
                                            {item.fldDescription}
                                        </option>
                                    ))}
                                </SelectGroup>
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldItemDescription">Item Description</Form.Label>
                                <TextInput
                                    name="fldItemDescription"
                                    id="fldItemDescription"
                                    placeholder="Item Description"
                                    pattern="^[A-Za-z0-9 ]+$"
                                    maxLength={50}
                                    errorMessage={{ pattern: 'No special characters are allowed' }}
                                    value={form.fldItemDescription ?? ''}
                                    onChange={handleChange}
                                    autoComplete="off"
                                />
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label>
                                    HSN Code<span className="text-danger">*</span>
                                </Form.Label>
                                <SearchableDropDown
                                    name="fldFKHSNCode"
                                    data={hsnMaster || []}
                                    value={form.fldFKHSNCode}
                                    onChange={(e) => applyHsn(e.value)}
                                    valueKey={(item) => item?.fldId}
                                    labelBuilder={(item) =>
                                        `${item?.tblPropertyCategoryId?.fldDescription ?? ''} (${item?.fldSalesCode}) - ${item?.fldSalesPct} %`
                                    }
                                />
                            </Form.Group>

                            <Form.Group as={Col} md="3">
                                <Form.Label>
                                    Color<span className="text-danger">*</span>
                                </Form.Label>
                                <SearchableDropDown
                                    name="fldFKColour"
                                    data={colorDetailsWithoutId || []}
                                    value={form.fldFKColour}
                                    onChange={(e) => setField('fldFKColour', e.value)}
                                    valueKey={(item) => item?.fldId}
                                    labelBuilder={(item) => `${item?.fldDescription}`}
                                />
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label>
                                    Size<span className="text-danger">*</span>
                                </Form.Label>
                                <SearchableDropDown
                                    name="fldFKSize"
                                    data={accessoriesSize || []}
                                    value={form.fldFKSize}
                                    onChange={(e) => {
                                        setField('fldFKSize', e.value);
                                        setField('fldSize', e.label);
                                    }}
                                    valueKey={(item) => item?.fldId}
                                    labelBuilder={(item) => `${item?.fldDescription}`}
                                />
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label>
                                    UOM<span className="text-danger">*</span>
                                </Form.Label>
                                <SearchableDropDown
                                    name="fldFKUOM"
                                    data={unitOfMeasurement || []}
                                    value={form.fldFKUOM}
                                    onChange={(e) => setField('fldFKUOM', e.value)}
                                    valueKey={(item) => item?.fldId}
                                    labelBuilder={(item) => `${item?.fldDescription}`}
                                />
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldQuantity">
                                    Quantity<span className="text-danger">*</span>
                                </Form.Label>
                                <TextInput
                                    name="fldQuantity"
                                    id="fldQuantity"
                                    placeholder="Quantity"
                                    pattern="^\d+(\.\d{1,4})?$"
                                    errorMessage={{ pattern: '*Only Numbers and Decimal values allowed.' }}
                                    style={{ textAlign: 'right' }}
                                    required
                                    value={form.fldQuantity ?? ''}
                                    onChange={handleChange}
                                    autoComplete="off"
                                />
                            </Form.Group>

                            {/* <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldRate">
                                    Rate (Rs.)<span className="text-danger">*</span>
                                </Form.Label>
                                <TextInput
                                    name="fldRate"
                                    id="fldRate"
                                    placeholder="Rate"
                                    pattern="^\d+(\.\d{1,4})?$"
                                    errorMessage={{ pattern: '*Only Numbers and Decimal values allowed.' }}
                                    style={{ textAlign: 'right' }}
                                    required
                                    value={form.fldRate ?? ''}
                                    onChange={handleChange}
                                    autoComplete="off"
                                />
                            </Form.Group>
                            {readOnlyAmount('fldGrossValue', 'Gross Value (Rs.)', totals.fldGrossValue)}
                            <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldDiscountPercentage">Discount Percentage</Form.Label>
                                <TextInput
                                    name="fldDiscountPercentage"
                                    id="fldDiscountPercentage"
                                    placeholder="Discount %"
                                    pattern="^\d+(\.\d{1,2})?$"
                                    errorMessage={{ pattern: '*Only Numbers and Decimal values allowed.' }}
                                    style={{ textAlign: 'right' }}
                                    value={form.fldDiscountPercentage ?? ''}
                                    onChange={handleChange}
                                    autoComplete="off"
                                />
                            </Form.Group>
                            <Form.Group as={Col} md="3">
                                <Form.Label htmlFor="fldDiscountValue">Discount Value (Rs.)</Form.Label>
                                <TextInput
                                    name="fldDiscountValue"
                                    id="fldDiscountValue"
                                    placeholder="Discount Value"
                                    pattern="^\d+(\.\d{1,2})?$"
                                    errorMessage={{ pattern: '*Only Numbers and Decimal values allowed.' }}
                                    style={{ textAlign: 'right' }}
                                    disabled={num(form.fldDiscountPercentage) > 0}
                                    value={num(form.fldDiscountPercentage) > 0 ? totals.fldDiscountValue.toFixed(2) : form.fldDiscountValue ?? ''}
                                    onChange={handleChange}
                                    autoComplete="off"
                                />
                            </Form.Group>

                            {readOnlyAmount('fldTaxableValue', 'Taxable Value (Rs.)', totals.fldTaxableValue)}
                            <Form.Group as={Col} md="3">
                                <Form.Label>IGST%</Form.Label>
                                <input className="form-control" style={{ textAlign: 'right' }} disabled value={num(form.fldIGSTPercentage)} />
                            </Form.Group>
                            {readOnlyAmount('fldIGSTValue', 'IGST Value (Rs.)', totals.fldIGSTValue)}
                            <Form.Group as={Col} md="3">
                                <Form.Label>SGST%</Form.Label>
                                <input className="form-control" style={{ textAlign: 'right' }} disabled value={num(form.fldSGSTPercentage)} />
                            </Form.Group>

                            {readOnlyAmount('fldSGSTValue', 'SGST Value (Rs.)', totals.fldSGSTValue)}
                            <Form.Group as={Col} md="3">
                                <Form.Label>CGST%</Form.Label>
                                <input className="form-control" style={{ textAlign: 'right' }} disabled value={num(form.fldCGSTPercentage)} />
                            </Form.Group>
                            {readOnlyAmount('fldCGSTValue', 'CGST Value (Rs.)', totals.fldCGSTValue)}
                            {readOnlyAmount('fldGSTValue', 'GST Value (Rs.)', totals.fldGSTValue)}

                            {readOnlyAmount('fldTotalValue', 'Total Value (Rs.)', totals.fldTotalValue)} */}
                            {/* <Form.Group as={Col} md="9">
                                <Form.Label htmlFor="fldRemarks">Remark</Form.Label>
                                <TextInput
                                    name="fldRemarks"
                                    id="fldRemarks"
                                    placeholder="Remark"
                                    maxLength={250}
                                    value={form.fldRemarks ?? ''}
                                    onChange={handleChange}
                                    autoComplete="off"
                                />
                            </Form.Group> */}
                        </Form.Row>

                        {mode === 'create' && (
                            <Form.Group as={Col} md="3" style={{ marginLeft: '41%', fontWeight: 'bold' }}>
                                <Checkbox
                                    name="fldIsReset"
                                    label="Clear All Data"
                                    checked={!keepValues}
                                    value={!keepValues}
                                    onChange={() => setKeepValues(!keepValues)}
                                />
                            </Form.Group>
                        )}

                        <Form.Row>
                            <Form.Group as={Col} sm={12} className="d-flex justify-content-around">
                                <Button variant="secondary" onClick={closeModal}>
                                    Close
                                </Button>
                                {mode === 'create' ? (
                                    <>
                                        <Button type="submit" variant="primary" name="addNew" value="addNew">
                                            Save & Add New
                                        </Button>
                                        <Button type="submit" variant="info" name="Create" value="Create">
                                            Save & Close
                                        </Button>
                                    </>
                                ) : (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={(props) => <Tooltip {...props}>{isLocked ? 'Goods already inwarded' : 'Save changes'}</Tooltip>}
                                    >
                                        <span>
                                            <Button type="submit" variant="info" name="Update" value="Update" disabled={isLocked}>
                                                Update
                                            </Button>
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </Form.Group>
                        </Form.Row>
                    </ValidationForm>
                </Modal.Body>
            </Modal>
        </Row>
    );
};

export default GatePassDetails;
